import React, { useState, useEffect } from 'react';
import './Compare.css';
import CompareSelect from '../../compare/CompareSelect/CompareSelect';
import { setChartTwoData } from '../../../functions/setChartData';
import coinMarketData from '../../../services/coinMarketData';
import Header from '../../common/Header/Header';
import List from '../../dashboard/List/List';
import Container from '@mui/material/Container';
import SelectDays from '../../coin/SelectDays/SelectDays';
import LineChart from '../../chart/LineChart/LineChart';
import ChartType from '../../coin/ChartType/ChartType';
import Grid2 from '@mui/material/Unstable_Grid2';
import Grid from '../../dashboard/Grid/Grid';
import { useSelector } from "react-redux";
import Footer from '../../common/Footer/Footer';
import scrollToTop from '../../../functions/scrollToTop';

// Exported Compare component
export const Compare = () => {
    // Redux state to get the list of coins
    const coins = useSelector((state) => state.coins);

    // State variables for the selected coins, their data, market data, loading status, and chart data
    const [coinOne, setCoinOne] = useState("bitcoin");
    const [coinTwo, setCoinTwo] = useState("ethereum");
    const [coinOneData, setCoinOneData] = useState(undefined);
    const [coinTwoData, setCoinTwoData] = useState(undefined);
    const [marketDataOne, setMarketDataOne] = useState(undefined);
    const [marketDataTwo, setMarketDataTwo] = useState(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [chart, setChart] = useState({});
    const [isChartLoading, setIsChartLoading] = useState(true);
    const [days, setDays] = useState(60);
    const [chartType, setChartType] = useState("prices");

    // Set the document title
    document.title = `Compare | InsightCoin`;

    // Scroll to the top of the page when the component mounts
    useEffect(() => {
        scrollToTop();
    }, []);

    // Fetch coin data when the coins array changes
    useEffect(() => {
        getCoinData(coinOne, setCoinOneData);
        getCoinData(coinTwo, setCoinTwoData);
    }, [coins]);

    // Fetch coin data and market data for coin one when the coinOne state changes
    useEffect(() => {
        getCoinData(coinOne, setCoinOneData);
        getMarket(coinOne, days, setMarketDataOne);
    }, [coinOne, days]);

    // Fetch coin data and market data for coin two when the coinTwo state changes
    useEffect(() => {
        getCoinData(coinTwo, setCoinTwoData);
        getMarket(coinTwo, days, setMarketDataTwo);
    }, [coinTwo, days]);

    // Toggle loading state when both coin data is available
    useEffect(() => {
        if (coinOneData && coinTwoData) {
            setIsLoading(false);
        }
    }, [coinOneData, coinTwoData]);

    // Fetch market data for both coins when the number of days changes
    useEffect(() => {
        getMarket(coinOne, days, setMarketDataOne);
        getMarket(coinTwo, days, setMarketDataTwo);
    }, [days]);

    // Update the chart data when the market data for both coins is available
    useEffect(() => {
        if (marketDataOne && marketDataTwo) {
            setChartTwoData(setChart, marketDataOne[chartType], marketDataTwo[chartType], coinOne, coinTwo);
            setIsChartLoading(false);
        }
    }, [marketDataOne, marketDataTwo, chartType]);

    // Fetch coin data based on the provided ID
    async function getCoinData(id, fun) {
        if (coins.length > 0) {
            let data = [...coins].filter((elem) => elem.id === id);
            fun(data[0]);
        }
    }

    // Fetch market data for a specific coin
    async function getMarket(c, d, fun) {
        const market = await coinMarketData(c, d);
        if (market) {
            fun(market);
        }
    }

    // Event handler for changing the selected first coin
    const handleCoinOneChange = (event) => {
        setCoinOne(event.target.value);
    }

    // Event handler for changing the selected second coin
    const handleCoinTwoChange = (event) => {
        setCoinTwo(event.target.value);
    }

    // Event handler for changing the selected number of days
    const handleDaysChange = async (event) => {
        setDays(event.target.value);
    };

    // Event handler for changing the selected chart type
    const handleChartTypeChange = async (event) => {
        setChartType(event.target.value);
    };

    // JSX structure for the Compare component
    return (
        <div>
            {/* Header component */}
            <Header />
            <br />
            {/* Container for selecting and comparing two coins */}
            <Container maxWidth="lg">
                <CompareSelect coinOne={coinOne} coinTwo={coinTwo} handleCoinOneChange={handleCoinOneChange} handleCoinTwoChange={handleCoinTwoChange} />
            </Container>
            <br />
            {/* Check if data is still loading, display information otherwise */}
            {isLoading ? <></> :
                <>
                    {/* Container for displaying information in both list and grid views */}
                    <Container maxWidth="lg">
                        <div className="list-view-67">
                            <List coin={coinOneData} index={0} />
                            <List coin={coinTwoData} index={1} />
                        </div>
                        <div className="grid-view-67">
                            <Grid coin={coinOneData} index={0} />
                            <br />
                            <Grid coin={coinTwoData} index={1} />
                        </div>
                    </Container>
                    <br />
                    {/* Container for selecting days and chart type */}
                    <Container maxWidth="lg">
                        {/* Check if chart data is still loading, display information otherwise */}
                        {isChartLoading ? <></> :
                            <>
                                <Grid2 container spacing={0} >
                                    <Grid2 xs={12} sm={3} md={3} >
                                        {/* SelectDays component for choosing the number of days */}
                                        <SelectDays days={days} handleDaysChange={handleDaysChange} />
                                    </Grid2>
                                    <Grid2 xs={12} sm={1} md={3} >

                                    </Grid2>
                                    <Grid2 xs={12} sm={8} md={6}>
                                        <br />
                                        {/* ChartType component for choosing the chart type */}
                                        <ChartType chartType={chartType} handleChartTypeChange={handleChartTypeChange} />
                                    </Grid2>
                                </Grid2>
                                <br />
                                {/* LineChart component for visualizing market data */}
                                <LineChart chartData={chart} chartType={chartType} />
                                <br />
                            </>
                        }
                    </Container>
                </>
            }
            {/* Footer component */}
            <Footer />
        </div>
    );
};
